import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScoreRow } from '@/components/ui/score-row';
import {
  useMatchDetail,
  useGeneratePitch,
  useDeleteMatch,
  useMatchAssignment
} from '@/hooks/use-matches';
import { formatDate } from '@/lib/format';
import {
  CheckCircle,
  Lightbulb,
  Loader2,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

const PITCH_TEMPLATES = [
  { id: 'professional', label: 'Professional' },
  { id: 'technical', label: 'Technical' },
  { id: 'client-facing', label: 'Client-Facing' },
  { id: 'consultative', label: 'Consultative' },
  { id: 'conversational', label: 'Conversational' }
] as const;

interface MatchDetailDialogProps {
  matchId: string | null;
  onClose: () => void;
  profileName?: string;
  assignmentTitle?: string;
}

export function MatchDetailDialog({
  matchId,
  onClose,
  profileName,
  assignmentTitle
}: MatchDetailDialogProps) {
  const { data, isLoading } = useMatchDetail(matchId);
  const generatePitch = useGeneratePitch(matchId ?? '');
  const deleteMatch = useDeleteMatch();
  const redoMatch = useMatchAssignment();
  const [template, setTemplate] = useState('professional');
  const [customInstructions, setCustomInstructions] = useState('');
  const [copied, setCopied] = useState(false);

  const match = data?.data;
  const details = match?.details;

  function handleCopyPitch() {
    if (!details?.pitch) return;
    navigator.clipboard.writeText(details.pitch as string);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCopyForChatGPT() {
    if (!match || !details) return;
    const text = [
      `Candidate: ${profileName ?? 'Unknown'}`,
      `Assignment: ${assignmentTitle ?? 'Unknown'}`,
      `Score: ${match.aiScore ?? match.heuristicScore ?? 'N/A'}`,
      '',
      'Why This Matches:',
      ...(details.reasons as string[]).map((r) => `- ${r}`),
      '',
      'Skills Match: ' + (details.skillsMatch as string[]).join(', '),
      'Skills Gap: ' + (details.skillsGap as string[]).join(', '),
      '',
      'Recommendations:',
      ...(details.recommendations as string[]).map((r) => `- ${r}`),
      details.pitch ? '\nPitch:\n' + details.pitch : ''
    ].join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleDelete() {
    if (!matchId) return;
    deleteMatch.mutate(matchId, { onSuccess: onClose });
  }

  function handleRedo() {
    if (!match) return;
    redoMatch.mutate({ profileId: match.profileId, assignmentId: match.assignmentId });
  }

  return (
    <Dialog open={!!matchId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">Match Details</DialogTitle>
          <DialogDescription>
            {profileName && assignmentTitle
              ? `${profileName} → ${assignmentTitle}`
              : profileName || assignmentTitle || 'AI match analysis'}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !match ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Match not found</div>
        ) : (
          <>
            <div className="mt-4 space-y-5">
              {/* Score cards */}
              <ScoreRow
                heuristicScore={match.heuristicScore}
                aiScore={match.aiScore}
                teamFitScore={match.teamFitScore}
                layout="grid"
              />

              <p className="text-xs text-muted-foreground">Matched {formatDate(match.matchedAt)}</p>

              {redoMatch.data?.data?.aiError && (
                <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <p className="font-semibold">AI analysis failed — previous results kept.</p>
                    <p className="mt-0.5 opacity-90">{redoMatch.data.data.aiError}</p>
                  </div>
                </div>
              )}

              {/* Link to full assignment details */}
              <Link to={`/my-assignments?assignment=${match.assignmentId}`} target="_blank">
                <Button size="sm" variant="outline" className="w-full">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  View Full Assignment
                </Button>
              </Link>

              {/* Score Breakdown */}
              {details?.breakdown &&
                Object.keys(details.breakdown as Record<string, number>).length > 0 && (
                  <details className="group">
                    <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground">
                      Score Breakdown ▸
                    </summary>
                    <div className="mt-2 space-y-1.5">
                      {Object.entries(details.breakdown as Record<string, number>)
                        .filter(
                          ([key]) =>
                            !['rawScore', 'profileSkillCount', 'assignmentSkillCount'].includes(key)
                        )
                        .map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2 text-xs">
                            <span className="w-32 truncate text-muted-foreground capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <div className="flex-1 h-1.5 rounded-full bg-muted">
                              <div
                                className="h-1.5 rounded-full bg-primary"
                                style={{
                                  width: `${Math.min(100, typeof value === 'number' ? value : 0)}%`
                                }}
                              />
                            </div>
                            <span className="w-10 text-right font-medium">{value}%</span>
                          </div>
                        ))}
                    </div>
                  </details>
                )}

              {/* Why This Matches */}
              {details?.reasons && (details.reasons as string[]).length > 0 && (
                <div className="rounded-lg bg-green-50 p-4 dark:bg-green-950/30">
                  <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-green-800 dark:text-green-300">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Why This Matches
                  </h4>
                  <ul className="space-y-1.5 text-xs text-green-900 dark:text-green-200">
                    {(details.reasons as string[]).map((reason, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-0.5 text-green-500">•</span>
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills Analysis */}
              {((details?.skillsMatch && (details.skillsMatch as string[]).length > 0) ||
                (details?.skillsGap && (details.skillsGap as string[]).length > 0)) && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {details?.skillsMatch && (details.skillsMatch as string[]).length > 0 && (
                    <div className="rounded-lg bg-green-50 p-4 dark:bg-green-950/30">
                      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-green-800 dark:text-green-300">
                        <CheckCircle className="h-3.5 w-3.5" />
                        Skills Match
                      </h4>
                      <ul className="space-y-1.5 text-xs text-green-900 dark:text-green-200">
                        {(details.skillsMatch as string[]).map((skill, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="mt-0.5 text-green-500">•</span>
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details?.skillsGap && (details.skillsGap as string[]).length > 0 && (
                    <div className="rounded-lg bg-red-50 p-4 dark:bg-red-950/30">
                      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-red-800 dark:text-red-300">
                        <CheckCircle className="h-3.5 w-3.5" />
                        Skills Gap
                      </h4>
                      <ul className="space-y-1.5 text-xs text-red-900 dark:text-red-200">
                        {(details.skillsGap as string[]).map((skill, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="mt-0.5 text-red-500">•</span>
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Recommendations */}
              {details?.recommendations && (details.recommendations as string[]).length > 0 && (
                <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-950/30">
                  <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                    <Lightbulb className="h-3.5 w-3.5" />
                    Recommendations
                  </h4>
                  <ul className="space-y-1.5 text-xs text-yellow-900 dark:text-yellow-200">
                    {(details.recommendations as string[]).map((rec, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-0.5">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Team Fit Details */}
              {details?.teamImpact && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold">Team Impact</h4>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {details.teamImpact as string}
                  </p>
                </div>
              )}

              {details?.gapsFilled && (details.gapsFilled as unknown[]).length > 0 && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold text-green-600 dark:text-green-400">
                    Gaps Filled
                  </h4>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {(
                      details.gapsFilled as unknown as Array<{ skill: string; impact: string }>
                    ).map((gap, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-green-500">+</span>
                        <span>
                          <strong>{gap.skill || String(gap)}</strong>
                          {gap.impact ? ` — ${gap.impact}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {details?.redundancies && (details.redundancies as unknown[]).length > 0 && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold text-yellow-600 dark:text-yellow-400">
                    Redundancies
                  </h4>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {(
                      details.redundancies as unknown as Array<{
                        skill: string;
                        assessment: string;
                      }>
                    ).map((r, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-yellow-500">~</span>
                        <span>
                          <strong>{r.skill || String(r)}</strong>
                          {r.assessment ? ` — ${r.assessment}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Generated Pitch */}
              {details?.pitch && (
                <div className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-xs font-semibold">Generated Pitch</h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={handleCopyPitch}
                    >
                      {copied ? (
                        <Check className="mr-1 h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="mr-1 h-3 w-3" />
                      )}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                    {details.pitch as string}
                  </p>
                </div>
              )}

              {/* Pitch Generation */}
              <div className="rounded-lg border p-4 space-y-3">
                <h4 className="text-xs font-semibold">
                  {details?.pitch ? 'Regenerate Pitch' : 'Generate Pitch'}
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {PITCH_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setTemplate(t.id);
                        setCustomInstructions('');
                      }}
                      className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                        template === t.id && !customInstructions
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="Or write custom instructions for the pitch style..."
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring resize-none"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      generatePitch.mutate({
                        language: 'en',
                        template: customInstructions ? undefined : template,
                        instructions: customInstructions || undefined
                      })
                    }
                    disabled={generatePitch.isPending}
                  >
                    {generatePitch.isPending ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate (EN)'
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      generatePitch.mutate({
                        language: 'sv',
                        template: customInstructions ? undefined : template,
                        instructions: customInstructions || undefined
                      })
                    }
                    disabled={generatePitch.isPending}
                  >
                    Svenska
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyForChatGPT}
                  disabled={!details}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {copied ? 'Copied!' : 'Copy for AI chat'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRedo}
                  disabled={redoMatch.isPending}
                >
                  {redoMatch.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  {redoMatch.isPending ? 'Redoing...' : 'Redo Match'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 dark:text-red-400"
                  onClick={handleDelete}
                  disabled={deleteMatch.isPending}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
